import React, { useState, useEffect } from 'react';
import { useParams, useHistory } from 'react-router-dom';
import { toast } from 'react-toastify';
import PageHeader from '../../components/PageHeader/PageHeader';
import ButtonGeneral from '../../components/buttons/ButtonGeneral';
import DefaultCard from '../../components/default/DefaultCard';
import useOfficialPublicationService from '../../app/services/hooks/useOfficialPublicationService';
import { ServiceResponse } from '../../app/services/shared/interfaces';

const PublicationPaymentSuccess: React.FC = () => {
    const { publicationId } = useParams<{ publicationId: string }>();
    const history = useHistory();
    const { getPublicationPaymentStatus } = useOfficialPublicationService();
    
    const [loading, setLoading] = useState(true);
    const [publicationData, setPublicationData] = useState<any>(null);
    const [brandData, setBrandData] = useState<any>(null);
    const [paymentStatus, setPaymentStatus] = useState<string>('pending');
    const [hasAccess, setHasAccess] = useState(true);

    useEffect(() => {
        loadPublicationData();
    }, [publicationId]);

    const loadPublicationData = () => {
        if (!publicationId) {
            toast.error('ID de publicación no válido');
            setLoading(false);
            return;
        }

        getPublicationPaymentStatus(parseInt(publicationId), {
            onSuccess: (response: ServiceResponse) => {
                if (response.data) {
                    setPublicationData(response.data.publication);
                    setBrandData(response.data.brand_registration);
                    setPaymentStatus(response.data.publication.payment_status);
                }
                setLoading(false);
            },
            onError: (response: ServiceResponse) => {
                if (response.title === 'Forbidden') {
                    setHasAccess(false);
                    toast.error('No tienes permisos para ver esta información');
                } else {
                    toast.error(response.message || 'Error al cargar la información');
                }
                setLoading(false);
            }
        });
    };

    const handleBackToStages = () => {
        if (brandData) {
            history.push(`/intranet/brand-registration/stages/${brandData.id}`);
        } else {
            history.push('/intranet/brand-registration');
        }
    };

    const handleBackToPublication = () => {
        if (brandData) {
            history.push(`/intranet/brand-registration/edit/${brandData.id}/step/5`);
        } else {
            history.push('/intranet/brand-registration');
        }
    };

    const formatPrice = (price: number) => {
        return new Intl.NumberFormat('es-CL', {
            style: 'currency',
            currency: 'CLP',
            minimumFractionDigits: 0
        }).format(price);
    };

    const renderContent = () => {
        if (paymentStatus === 'paid') {
            return (
                <>
                    <div className="text-center mb-4">
                        <div className="mb-3">
                            <i className="fas fa-check-circle text-success" style={{ fontSize: '4rem' }}></i>
                        </div>
                        <h4 className="text-success mb-2">¡Pago Exitoso!</h4>
                        <p className="text-muted mb-0">
                            Tu pago ha sido procesado correctamente. La publicación en el Diario Oficial se gestionará según el cronograma establecido.
                        </p>
                    </div>

                    {/* Información de la marca */}
                    {brandData && (
                        <div className="card border-0 shadow-sm mb-4">
                            <div className="card-header bg-light border-0">
                                <h6 className="mb-0 text-dark fw-bold">Detalles de la Solicitud</h6>
                            </div>
                            <div className="card-body">
                                <div className="row g-3">
                                    <div className="col-md-4">
                                        <small className="text-muted d-block mb-1">Solicitud</small>
                                        <strong className="text-dark">#{brandData.application_number}</strong>
                                    </div>
                                    <div className="col-md-4">
                                        <small className="text-muted d-block mb-1">Marca</small>
                                        <strong className="text-dark">{brandData.brand_name}</strong>
                                    </div>
                                    <div className="col-md-4">
                                        <small className="text-muted d-block mb-1">Solicitante</small>
                                        <strong className="text-dark">{brandData.applicant_name}</strong>
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Información del pago */}
                    {publicationData && (
                        <div className="card border-0 shadow-sm mb-4" style={{ backgroundColor: '#d1fae5' }}>
                            <div className="card-body">
                                <div className="d-flex align-items-start mb-3">
                                    <i className="fas fa-check-circle text-success me-3" style={{ fontSize: '1.5rem' }}></i>
                                    <div className="flex-grow-1">
                                        <h6 className="mb-2 text-success fw-bold">Pago Procesado</h6>
                                        <p className="mb-3 text-dark" style={{ fontSize: '0.95rem' }}>
                                            El pago de la publicación en el Diario Oficial ha sido confirmado.
                                        </p>
                                        
                                        <div className="row g-2">
                                            <div className="col-md-4">
                                                <div className="bg-white rounded p-2 text-center">
                                                    <small className="text-muted d-block">Monto pagado</small>
                                                    <strong className="text-dark d-block">{formatPrice(publicationData.publication_fee)}</strong>
                                                </div>
                                            </div>
                                            <div className="col-md-4">
                                                <div className="bg-white rounded p-2 text-center">
                                                    <small className="text-muted d-block">Estado</small>
                                                    <span className="badge bg-success mt-1">Pagado</span>
                                                </div>
                                            </div>
                                            {publicationData.payment_date && (
                                                <div className="col-md-4">
                                                    <div className="bg-white rounded p-2 text-center">
                                                        <small className="text-muted d-block">Fecha de pago</small>
                                                        <strong className="text-dark d-block" style={{ fontSize: '0.9rem' }}>
                                                            {new Date(publicationData.payment_date).toLocaleDateString('es-CL', {
                                                                day: '2-digit',
                                                                month: '2-digit',
                                                                year: 'numeric'
                                                            })}
                                                        </strong>
                                                    </div>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Próximos pasos */}
                    <div className="card border-0 shadow-sm mb-4" style={{ backgroundColor: '#dbeafe' }}>
                        <div className="card-body">
                            <div className="d-flex align-items-start">
                                <i className="fas fa-info-circle text-info me-3" style={{ fontSize: '1.5rem' }}></i>
                                <div className="flex-grow-1">
                                    <h6 className="mb-2 text-info fw-bold">Próximos Pasos</h6>
                                    <ul className="mb-0 ps-3" style={{ fontSize: '0.95rem' }}>
                                        <li className="mb-2">Tu marca será publicada en el Diario Oficial</li>
                                        <li className="mb-2">Se abrirá un período de oposición de 30 días hábiles</li>
                                        <li className="mb-2">Recibirás notificaciones sobre el progreso del proceso</li>
                                        <li className="mb-0">Si no hay oposiciones, se procederá al examen de fondo</li>
                                    </ul>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className="d-flex gap-3 justify-content-end">
                        <ButtonGeneral
                            type="button"
                            variant="secondary"
                            size="medium"
                            onClick={handleBackToPublication}
                            title="Ver Publicación"
                            icon="fas fa-eye"
                            iconPosition="left"
                        />
                        <ButtonGeneral
                            type="button"
                            variant="primary"
                            size="medium"
                            onClick={handleBackToStages}
                            title="Volver a Mis Solicitudes"
                            icon="fas fa-arrow-right"
                            iconPosition="right"
                        />
                    </div>
                </>
            );
        }

        if (paymentStatus === 'cancelled') {
            return (
                <>
                    <div className="mb-4 text-center">
                        <div className="mb-3">
                            <i className="fas fa-times-circle text-warning" style={{ fontSize: '4rem' }}></i>
                        </div>
                        <h4 className="text-warning">Pago Cancelado</h4>
                        <p className="text-muted">
                            El pago fue cancelado. Puedes intentar nuevamente cuando estés listo.
                        </p>
                    </div>

                    <div className="alert alert-warning">
                        <h6 className="alert-heading">
                            <i className="fas fa-exclamation-triangle me-2"></i>
                            Pago No Completado
                        </h6>
                        <p className="mb-0">No se procesó el pago. La publicación en el Diario Oficial quedará pendiente hasta que se realice el pago correspondiente.</p>
                    </div>

                    <div className="d-flex gap-3 justify-content-end mt-4">
                        <ButtonGeneral
                            type="button"
                            variant="secondary"
                            size="medium"
                            onClick={handleBackToStages}
                            title="Volver a Mis Solicitudes"
                        />
                        <ButtonGeneral
                            type="button"
                            variant="primary"
                            size="medium"
                            onClick={handleBackToPublication}
                            title="Reintentar Pago"
                            icon="fas fa-redo"
                            iconPosition="left"
                        />
                    </div>
                </>
            );
        }

        if (paymentStatus === 'failed') {
            return (
                <>
                    <div className="mb-4 text-center">
                        <div className="mb-3">
                            <i className="fas fa-exclamation-triangle text-danger" style={{ fontSize: '4rem' }}></i>
                        </div>
                        <h4 className="text-danger">Error en el Pago</h4>
                        <p className="text-muted">
                            Ocurrió un error al procesar tu pago. Por favor, intenta nuevamente.
                        </p>
                    </div>

                    <div className="alert alert-danger">
                        <h6 className="alert-heading">
                            <i className="fas fa-times-circle me-2"></i>
                            Error al Procesar el Pago
                        </h6>
                        <p className="mb-0">Hubo un problema al procesar tu pago. Por favor verifica tu método de pago e intenta nuevamente.</p>
                    </div>

                    <div className="d-flex gap-3 justify-content-end mt-4">
                        <ButtonGeneral
                            type="button"
                            variant="secondary"
                            size="medium"
                            onClick={handleBackToStages}
                            title="Volver a Mis Solicitudes"
                        />
                        <ButtonGeneral
                            type="button"
                            variant="primary"
                            size="medium"
                            onClick={handleBackToPublication}
                            title="Reintentar Pago"
                            icon="fas fa-redo"
                            iconPosition="left"
                        />
                    </div>
                </>
            );
        }

        // Estado pendiente o desconocido
        return (
            <>
                <div className="mb-4 text-center">
                    <div className="mb-3">
                        <i className="fas fa-hourglass-half text-info" style={{ fontSize: '4rem' }}></i>
                    </div>
                    <h4 className="text-info">Procesando Pago</h4>
                    <p className="text-muted">
                        Estamos verificando el estado de tu pago. Por favor espera un momento.
                    </p>
                </div>

                <div className="d-flex gap-3 justify-content-end mt-4">
                    <ButtonGeneral
                        type="button"
                        variant="secondary"
                        size="medium"
                        onClick={handleBackToStages}
                        title="Volver a Mis Solicitudes"
                    />
                    <ButtonGeneral
                        type="button"
                        variant="primary"
                        size="medium"
                        onClick={() => loadPublicationData()}
                        title="Actualizar Estado"
                        icon="fas fa-sync"
                        iconPosition="left"
                    />
                </div>
            </>
        );
    };

    const getPageTitle = () => {
        if (paymentStatus === 'paid') return 'Pago Exitoso';
        if (paymentStatus === 'cancelled') return 'Pago Cancelado';
        if (paymentStatus === 'failed') return 'Error en el Pago';
        return 'Estado del Pago';
    };

    if (loading) {
        return (
            <div className="container-fluid">
                <PageHeader 
                    breadcrumbs={[
                        { label: 'Inicio', path: '/intranet' },
                        { label: 'Registro de Marca', path: '/intranet/brand-registration' },
                        { label: 'Pago', isCurrent: true }
                    ]}
                    title="Procesando..." 
                    description="Verificando estado del pago"
                />
                <div className="row justify-content-center">
                    <div className="col-md-6">
                        <DefaultCard>
                            <div className="text-center py-5">
                                <div className="spinner-border text-primary" role="status">
                                    <span className="visually-hidden">Cargando...</span>
                                </div>
                            </div>
                        </DefaultCard>
                    </div>
                </div>
            </div>
        );
    }

    if (!hasAccess) {
        return (
            <div className="container-fluid">
                <PageHeader 
                    breadcrumbs={[
                        { label: 'Inicio', path: '/intranet' },
                        { label: 'Registro de Marca', path: '/intranet/brand-registration' },
                        { label: 'Acceso Denegado', isCurrent: true }
                    ]}
                    title="Acceso Denegado" 
                    description="No tienes permisos para ver esta página"
                />
                <div className="row justify-content-center">
                    <div className="col-md-6">
                        <DefaultCard>
                            <div className="text-center py-5">
                                <i className="fas fa-lock text-danger mb-3" style={{ fontSize: '3rem' }}></i>
                                <p className="text-muted">No tienes permisos para acceder a esta información.</p>
                                <ButtonGeneral
                                    type="button"
                                    variant="primary"
                                    size="medium"
                                    onClick={() => history.push('/intranet/brand-registration')}
                                    title="Volver a Mis Solicitudes"
                                />
                            </div>
                        </DefaultCard>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="container-fluid">
            <PageHeader 
                breadcrumbs={[
                    { label: 'Inicio', path: '/intranet' },
                    { label: 'Registro de Marca', path: '/intranet/brand-registration' },
                    { label: getPageTitle(), isCurrent: true }
                ]}
                title={`${getPageTitle()} - Publicación Diario Oficial`}
                description={brandData ? `Marca: ${brandData.brand_name} - Solicitud #${brandData.application_number}` : 'Resultado del proceso de pago'}
            />
            
            <div className="row justify-content-center">
                <div className="col-md-8 col-lg-6">
                    <DefaultCard>
                        {renderContent()}
                    </DefaultCard>
                </div>
            </div>
        </div>
    );
};

export default PublicationPaymentSuccess;

